import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector, shallowEqual } from "react-redux";
import "./dashboard.scss";
import { getRoleDashboardConfig } from "./roleConfig";
import DashboardHeader from "./DashboardHeader";
import DashboardSummary from "./DashboardSummary";
import PrimaryInsight from "./PrimaryInsight";
import RecentActivity from "./RecentActivity";
import RoleSpecificSections from "./RoleSpecificSections";
import { ReportError, ReportLoading } from "./DashboardStates";

/**
 * Redesigned, role-aware Dashboard.
 *
 * Replaces frontend/src/_metronic/_partials/dashboards/{SaDashBoard,
 * SupplierDashboard,UserDashboard,Demo1Dashboard}.js with ONE
 * configuration-driven page (see roleConfig.js) instead of three
 * hand-duplicated dashboard implementations, while preserving the exact
 * same role-detection logic and the exact same underlying endpoints:
 *   - super          -> GET /v2/dashboard-sa      (+ GET /v2/company for a
 *                       company count, since dashboard-sa doesn't return one)
 *   - supplier       -> GET /v2/dashboard-supplier
 *   - everyone else  -> GET /v2/activities/dashboard/sr-wo
 * Recent activity (GET /v2/activities/:userId) and, where permitted, the
 * asset-condition / provider-performance mini widgets are unchanged from
 * their prior data sources.
 */
export default function DashboardPage() {
  const { user } = useSelector(state => state.auth.user, shallowEqual);
  const userdata = user && user;
  const permissions = user && user.userRole ? user.userRole.permission : [];
  const userRole = user && user.userRole ? user.userRole.name : null;

  // BUG FIX: getRoleDashboardConfig() returns a brand-new object (with new
  // function references) on every call. Calling it directly in the render
  // body meant `config` changed identity on every render, and several
  // effects below depended on `[config]` - so each state update they caused
  // (setCompanyCount/setAssetCondition/setProviders) triggered a re-render,
  // which produced a new `config` reference, which re-ran those same
  // effects again, refetching and re-rendering in a tight loop. That loop
  // was the dashboard's visible flickering (charts/cards destroying and
  // re-rendering continuously). Memoizing on the primitive role flags
  // (which only actually change when the logged-in user's role does) fixes
  // this: `config` is now stable across re-renders that don't change role.
  const config = useMemo(
    () => getRoleDashboardConfig(userdata),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userdata && userdata._id, userdata && userdata.super, userdata && userdata.supplier, userdata && userdata.ao, userdata && userdata.mro, userdata && userdata.imo, userdata && userdata.technician, userdata && userdata.extTechnician]
  );

  const [dashboardInfo, setDashboardInfo] = useState(null);
  const [companyCount, setCompanyCount] = useState(null);
  const [assetCondition, setAssetCondition] = useState(null);
  const [providers, setProviders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPrimary = () => {
    if (!config) return;
    setLoading(true);
    setError(null);
    axios
      .get(config.endpoint)
      .then(({ data }) => setDashboardInfo(data))
      .catch(() => setError("Couldn't load your dashboard data."))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchPrimary, [config && config.endpoint]);

  // Super Admin only: company count (see roleConfig.js's `extraMetric`).
  useEffect(() => {
    if (!config || config.role !== "super") return;
    axios
      .get("/v2/company")
      .then(({ data }) => setCompanyCount(Array.isArray(data) ? data.length : null))
      .catch(() => setCompanyCount(null));
  }, [config && config.role]);

  // Operations roles only, and only if permitted: asset condition + provider performance.
  useEffect(() => {
    if (!config || !config.showAssetCondition) return;
    axios
      .post("/v2/asset-report/asset", {})
      .then(({ data }) => setAssetCondition(data))
      .catch(() => setAssetCondition(null));
  }, [config && config.showAssetCondition]);

  useEffect(() => {
    if (!config || !config.showProviderPerformance) return;
    axios
      .get("/v2/score-card/all/list")
      .then(({ data }) => setProviders(data || []))
      .catch(() => setProviders(null));
  }, [config && config.showProviderPerformance]);

  if (!config) return null;

  const metrics = config.buildMetrics(dashboardInfo);
  if (config.extraMetric) {
    metrics.push({ ...config.extraMetric, value: companyCount });
  }

  const chartConfig = config.chart ? config.chart(dashboardInfo) : null;

  return (
    <div className="dashboard-page">
      <DashboardHeader title={config.title} subtitle={config.subtitle} />

      {error && <ReportError message={error} onRetry={fetchPrimary} />}

      {!error && loading && <ReportLoading />}

      {!error && !loading && (
        <>
          <DashboardSummary metrics={metrics} />

          <div className="dashboard-content">
            {chartConfig && (
              <PrimaryInsight
                title={chartConfig.title}
                subtitle={chartConfig.subtitle}
                categories={chartConfig.categories}
                series={chartConfig.series}
                colors={
                  chartConfig.series.some(s => s.color)
                    ? chartConfig.series.map(s => s.color).filter(Boolean)
                    : undefined
                }
              />
            )}
            <RecentActivity userId={userdata && userdata._id} />
          </div>

          <RoleSpecificSections
            permissions={permissions}
            userRole={userRole}
            assetCondition={assetCondition}
            providers={providers}
          />
        </>
      )}
    </div>
  );
}
